import {
  CreateStakingErc3643PoolResult,
  StakingErc3643Overview,
  StakingErc3643PoolApi,
  StakingErc3643PoolOverviewApi,
  StakingErc3643TransactionApi,
} from 'Modules'

import {
  STAKING_ERC3643_STATUS_FROM_API,
  STAKING_ERC3643_TX_FROM_API,
} from 'constants/staking-erc3643'
import {
  PoolDetailStats,
  PoolListOverview,
  PoolTransactionRow,
  RewardTokenType,
  StakingPoolRow,
} from 'pages/staking-erc-3643/types'

const PTO_THUMB_COLORS = ['#3B6FF5', '#12B981', '#F59E0B', '#0EA5A4', '#8B5CF6', '#EC4899']

export const ptoInitials = (symbol?: string | null) =>
  (symbol ?? 'PT').slice(0, 2).toUpperCase()

export const ptoThumbColor = (id: string) => {
  let hash = 0
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash + id.charCodeAt(i)) % PTO_THUMB_COLORS.length
  }
  return PTO_THUMB_COLORS[hash]
}

export const toPoolStakingParams = (args: CreateStakingErc3643PoolResult) => ({
  ptoAddress: args.ptoAddress,
  rewardToken: args.rewardToken,
  initialOwner: args.initialOwner,
  salt: args.salt,
  startTime: BigInt(args.startTime),
  endTime: BigInt(args.endTime),
  // Already raw units from BE; no cap on chain, so never rescale here
  rewardPerYear: BigInt(args.rewardPerYear),
  claimTopic: BigInt(args.claimTopic),
  claimScheme: BigInt(args.claimScheme),
  claimIssuer: args.claimIssuer,
  claimSignature: args.claimSignature,
  claimData: args.claimData,
  claimUri: args.claimUri ?? '',
})

export const mapPoolToRow = (pool: StakingErc3643PoolApi): StakingPoolRow => {
  const symbol = pool.tokenize?.symbol ?? 'PTO'
  const tokenizeId = pool.tokenize?._id ?? pool._id
  return {
    id: pool._id,
    ptoSymbol: symbol,
    ptoName: pool.tokenize?.fullName ?? symbol,
    ptoInitials: ptoInitials(symbol),
    ptoThumbnailColor: ptoThumbColor(tokenizeId),
    contractAddress: pool.contractAddress ?? pool.stakingAddress ?? '',
    rewardToken: (pool.rewardTokenSymbol ?? 'USDC') as RewardTokenType,
    rewardModel: pool.rewardModel ?? 'APR',
    rewardPerYear: pool.rewardPerYear ?? null,
    apr: pool.apr ?? null,
    totalStakers: pool.totalStakers ?? 0,
    stakedAmount: pool.stakedAmount ?? 0,
    stakedSymbol: symbol,
    startDate: pool.startAt,
    endDate: pool.endAt,
    status: STAKING_ERC3643_STATUS_FROM_API[pool.status] ?? 'Scheduled',
    display: pool.isDisplay,
    createdAt: pool.createdAt,
  }
}

export const mapOverview = (data: StakingErc3643Overview): PoolListOverview => ({
  totalValueStakedUsd: data.totalValueStaked ?? 0,
  totalStakers: data.totalStakers ?? 0,
  rewardClaimed: (data.rewards ?? []).map((row) => ({
    symbol: row.symbol as RewardTokenType,
    amount: row.totalRewardClaimed ?? 0,
  })),
  rewardUnclaimed: (data.rewards ?? []).map((row) => ({
    symbol: row.symbol as RewardTokenType,
    amount: row.totalRewardUnclaimed ?? null,
  })),
})

export const mapPoolOverview = (
  pool: StakingErc3643PoolApi,
  overview?: StakingErc3643PoolOverviewApi
): PoolDetailStats => {
  const symbol = pool.tokenize?.symbol ?? 'PTO'
  const stats = overview ?? pool.overview
  return {
    totalStakers: stats?.totalStakers ?? pool.totalStakers ?? 0,
    totalStakedAmount: stats?.totalStakedAmount ?? pool.stakedAmount ?? 0,
    stakedSymbol: symbol,
    totalRewardClaimed: stats?.totalRewardClaimed ?? 0,
    totalRewardUnclaimed: stats?.totalRewardUnclaimed ?? null,
    rewardUnit: (stats?.rewardTokenSymbol ?? pool.rewardTokenSymbol ?? 'USDC') as RewardTokenType,
  }
}

export const mapTransactionRow = (tx: StakingErc3643TransactionApi): PoolTransactionRow => ({
  id: tx._id,
  walletAddress: tx.walletAddress,
  type: STAKING_ERC3643_TX_FROM_API[tx.type] ?? 'Stake',
  amount: tx.amount,
  symbol: tx.symbol ?? '',
  txHash: tx.txHash,
  createdAt: tx.timestamp,
})
